<template>
  <el-popover placement="bottom-end" width="400" trigger="click" popper-class="nc-popper" v-model="open" @show="onOpen">
    <!-- 面板 -->
    <div class="nc">
      <div class="nc__head">
        <span class="nc__title">{{ $t("notifications.title") }}</span>
        <button v-if="unreadCount > 0" type="button" class="nc__markall" @click="markAllRead">
          {{ $t("notifications.markAll") }}
        </button>
      </div>

      <div v-loading="loading" class="nc__list">
        <template v-if="items.length > 0">
          <button v-for="n in items" :key="n.key" type="button" :class="['nc__item', { 'is-unread': !n.read }]" @click="onClick(n)">
            <span :class="['nc__dot', 'nc__dot--' + n.tone]"></span>
            <span :class="['nc__icon', 'nc__icon--' + n.tone]"><i :class="n.icon"></i></span>
            <span class="nc__body">
              <span class="nc__row1">
                <span class="nc__label">{{ n.title }}</span>
                <span class="nc__time vtm-num">{{ n.timeText }}</span>
              </span>
              <span class="nc__desc">{{ n.desc }}</span>
              <span v-if="n.machine" class="nc__machine">
                <i class="el-icon-cpu"></i> {{ n.machine }}
                <span v-if="n.deep" class="nc__deep">· {{ $t("notifications.view") }} ›</span>
              </span>
            </span>
          </button>
        </template>

        <div v-else-if="!loading" class="nc__empty">
          <i class="el-icon-success nc__empty-icon"></i>
          <div class="nc__empty-title">{{ $t("notifications.empty") }}</div>
          <div class="nc__empty-sub">{{ $t("notifications.emptyHint") }}</div>
        </div>
      </div>
    </div>

    <!-- 触发按钮（常驻头部） -->
    <button slot="reference" class="vtm-header__iconbtn" type="button" :title="$t('notifications.title')">
      <el-badge :value="unreadCount" :hidden="unreadCount === 0" :max="99">
        <i class="el-icon-bell"></i>
      </el-badge>
    </button>
  </el-popover>
</template>

<script>
  // 常驻通知中心：聚合设备报警（equipmentFaultRecord/page）。
  // 后端无「标记已读」接口，故已读状态按用户在本地(localStorage)记录；
  // 服务端 isLook==1 亦视为已读。
  const READ_KEY_PREFIX = "reyvend-notif-read";
  const POLL_MS = 60000;

  export default {
    name: "NotificationCenter",
    data() {
      return {
        open: false,
        loading: false,
        raw: [],
        readSet: {},
        timer: null
      };
    },
    computed: {
      storageKey() {
        const uid = (this.$store && this.$store.state.uid) || "anon";
        return `${READ_KEY_PREFIX}:${uid}`;
      },
      items() {
        return this.raw.map(a => {
          const key = this.alarmKey(a);
          const meta = this.classify(a);
          return {
            key,
            tone: meta.tone,
            icon: meta.icon,
            title: meta.title,
            desc: a.info || meta.title,
            machine: a.equipmentName || a.equipmentNo || "",
            equipmentNo: a.equipmentNo || "",
            deep: !!a.equipmentNo,
            timeText: this.relTime(a.createTime),
            read: String(a.isLook) === "1" || this.readSet[key] === true
          };
        });
      },
      unreadCount() {
        return this.items.filter(n => !n.read).length;
      }
    },
    mounted() {
      this.loadReadSet();
      this.fetch();
      this.timer = setInterval(this.fetch, POLL_MS);
    },
    beforeDestroy() {
      if (this.timer) {
        clearInterval(this.timer);
      }
    },
    methods: {
      // 唯一标识（后端无 id）：设备号 + 报警代码 + 时间
      alarmKey(a) {
        return [a.equipmentNo || "", a.code || "", a.type || "", a.createTime || a.buyTime || ""].join("|");
      },
      // 报警 → 语义色调/图标/清晰标题
      classify(a) {
        const code = String(a.code || "");
        const type = String(a.type || "");
        if (code === "400" || type === "4") {
          return { tone: "warn", icon: "el-icon-goods", title: this.$t("notifications.lowStock") };
        }
        if (type === "1") {
          const byCode = {
            "001": "notifications.aisleFault",
            "002": "notifications.photoEye",
            "003": "notifications.offline",
            "004": "notifications.deviceError",
            "005": "notifications.controller",
            "006": "notifications.verify",
            "009": "notifications.coinRefund"
          };
          return {
            tone: "crit",
            icon: code === "003" ? "el-icon-connection" : "el-icon-warning",
            title: this.$t(byCode[code] || "notifications.deviceFault")
          };
        }
        if (type === "2") {
          return { tone: "warn", icon: "el-icon-shopping-cart-full", title: this.$t("notifications.productFault") };
        }
        if (type === "3") {
          return { tone: "info", icon: "el-icon-video-camera", title: this.$t("notifications.adFault") };
        }
        return { tone: "info", icon: "el-icon-bell", title: this.$t("notifications.alert") };
      },
      relTime(t) {
        if (!t) {
          return "";
        }
        const then = new Date(String(t).replace(/-/g, "/")).getTime();
        if (!then) {
          return "";
        }
        const diff = Math.max(0, Date.now() - then);
        const m = Math.floor(diff / 60000);
        if (m < 1) {
          return this.$t("notifications.justNow");
        }
        if (m < 60) {
          return m + "m";
        }
        const h = Math.floor(m / 60);
        if (h < 24) {
          return h + "h";
        }
        return Math.floor(h / 24) + "d";
      },
      fetch() {
        this.loading = true;
        this.$api
          .request(this.$url.AlarmRecord.page, this.$method.get, { pageIndex: 1, pageSize: 20 })
          .then(res => {
            if (res.code === 0 && res.data) {
              this.raw = res.data.records || [];
            }
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
          });
      },
      onOpen() {
        this.fetch();
      },
      onClick(n) {
        this.markRead(n.key);
        if (n.equipmentNo) {
          this.open = false;
          const target = "/machine/user/machine-user";
          if (!(this.$route.path === target && this.$route.query.equipmentNo === n.equipmentNo)) {
            this.$router.push({ path: target, query: { equipmentNo: n.equipmentNo, autoSearch: true } });
          }
        }
      },
      loadReadSet() {
        try {
          const saved = JSON.parse(localStorage.getItem(this.storageKey));
          this.readSet = saved && typeof saved === "object" ? saved : {};
        } catch (e) {
          this.readSet = {};
        }
      },
      persistReadSet() {
        try {
          // 仅保留当前可见项，避免无限增长
          const keep = {};
          this.raw.forEach(a => {
            const k = this.alarmKey(a);
            if (this.readSet[k]) {
              keep[k] = true;
            }
          });
          this.readSet = keep;
          localStorage.setItem(this.storageKey, JSON.stringify(this.readSet));
        } catch (e) {
          /* ignore */
        }
      },
      markRead(key) {
        this.$set(this.readSet, key, true);
        this.persistReadSet();
      },
      markAllRead() {
        this.raw.forEach(a => {
          this.$set(this.readSet, this.alarmKey(a), true);
        });
        this.persistReadSet();
      }
    }
  };
</script>

<style scoped lang="scss">
  /* Header.vue 的 .vtm-header__iconbtn 是父组件的 scoped 样式，触达不到本组件模板
     内的按钮，所以在这里重复一份，保持与主题切换 / 全屏按钮一致的尺寸与 hover 效果 */
  .vtm-header__iconbtn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: none;
    border-radius: var(--vtm-r-sm);
    background: transparent;
    color: var(--vtm-ink-2);
    font-size: 17px;
    cursor: pointer;
    transition: background-color var(--vtm-speed) ease, color var(--vtm-speed) ease;
  }

  .vtm-header__iconbtn:hover {
    background: var(--vtm-well);
    color: var(--vtm-heading);
  }

  .vtm-header__iconbtn:focus-visible {
    outline: 2px solid var(--vtm-mint-dim);
    outline-offset: 1px;
  }

  .vtm-header__iconbtn .el-badge {
    line-height: 1;
  }

  .nc {
    margin: -12px;
  }

  .nc__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid var(--vtm-line);
  }

  .nc__title {
    font-family: var(--vtm-display);
    font-size: 14px;
    font-weight: 600;
    color: var(--vtm-heading);
  }

  .nc__markall {
    border: none;
    background: transparent;
    color: var(--vtm-mint-ink);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .nc__list {
    max-height: 420px;
    overflow-y: auto;
    min-height: 120px;
  }

  .nc__item {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    width: 100%;
    box-sizing: border-box;
    padding: 12px 16px 12px 20px;
    border: none;
    border-bottom: 1px solid var(--vtm-line);
    background: transparent;
    text-align: left;
    cursor: pointer;
    transition: background-color var(--vtm-speed) ease;

    &:hover {
      background: var(--vtm-well);
    }

    &.is-unread {
      background: var(--vtm-mint-soft);
    }
    &.is-unread:hover {
      background: var(--vtm-mint-soft);
    }
  }

  .nc__dot {
    position: absolute;
    left: 8px;
    top: 17px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: transparent;
  }
  .nc__item.is-unread .nc__dot--crit {
    background: var(--vtm-crit);
  }
  .nc__item.is-unread .nc__dot--warn {
    background: var(--vtm-warn);
  }
  .nc__item.is-unread .nc__dot--info {
    background: var(--vtm-info);
  }

  .nc__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: var(--vtm-r-sm);
    font-size: 16px;

    &--crit {
      background: var(--vtm-crit-soft);
      color: var(--vtm-crit);
    }
    &--warn {
      background: var(--vtm-warn-soft);
      color: var(--vtm-warn);
    }
    &--info {
      background: var(--vtm-info-soft);
      color: var(--vtm-info);
    }
  }

  .nc__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .nc__row1 {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
  }

  .nc__label {
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-ink);
  }

  .nc__time {
    font-size: 11px;
    color: var(--vtm-ink-3);
    flex-shrink: 0;
  }

  .nc__desc {
    font-size: 12px;
    color: var(--vtm-ink-2);
    line-height: 1.4;
    word-break: break-word;
  }

  .nc__machine {
    font-size: 11.5px;
    color: var(--vtm-ink-3);
    margin-top: 1px;

    i {
      font-size: 12px;
    }
  }

  .nc__deep {
    color: var(--vtm-mint-ink);
    font-weight: 600;
  }

  .nc__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 40px 20px;
    text-align: center;
  }

  .nc__empty-icon {
    font-size: 32px;
    color: var(--vtm-ok);
  }

  .nc__empty-title {
    font-family: var(--vtm-display);
    font-size: 14px;
    font-weight: 600;
    color: var(--vtm-heading);
  }

  .nc__empty-sub {
    font-size: 12px;
    color: var(--vtm-ink-3);
  }
</style>
